import { useEffect, useMemo, useState } from "react"
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet"
import MarkerClusterGroup from "react-leaflet-cluster"
import "leaflet/dist/leaflet.css"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"

import L from "leaflet"
import { faBuilding, faHouse, faLocationDot, faTag, faTimes } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@/components/button"
import { Establishment } from "../model/establishment.types"
import { PhysicalRisk } from "@/entities/physical-risk/model/physical-risk.types"
import { RISK_RESIDUAL_SCALES } from "@/redux/account.types"
import getColor from "@/utils/get-color.utils"

interface Props {
  establishments: Establishment[]
  onConnect?: (id: string) => void
}

interface EstablishmentMapItem {
  id: string
  name: string
  address: string
  color: string
  riskScore: number
  siret: string
  naf: string
  position: [number, number]
}

const FRANCE_CENTER: [number, number] = [46.603354, 1.888334]
const MARKER_COLOR = "#777777"
const MARKER_BORDER_COLOR = "#ffffff"
const MARKER_TEXT_COLOR = "#ffffff"
const DETACHED_CLUSTER_DISTANCE_KM = 140
const MIN_DETACHED_DISTANCE_KM = 180
const MIN_MAIN_CLUSTER_SIZE = 5
const MIN_MAIN_CLUSTER_RATIO = 0.7
const MAX_DETACHED_COMPONENT_RATIO = 0.2
const MAX_DETACHED_MAPS = 2

type DetachedMapCorner = "left" | "right"

interface DetachedMap {
  id: string
  items: EstablishmentMapItem[]
  bounds: L.LatLngBounds
  corner: DetachedMapCorner
}

function createPinHtml({ color = MARKER_COLOR, count, size }: { color?: string; count?: number; size: number }) {
  const tailSize = Math.round(size * 0.3)
  const height = size + tailSize
  const fontSize = Math.max(13, Math.round(size * 0.34))
  const tailTop = Math.round(size - tailSize / 2 - 2)

  return `
    <div
      style="
        position:relative;
        width:${size}px;
        height:${height}px;
        filter:drop-shadow(0 4px 8px rgba(15,23,42,0.24));
      "
    >
      <div
        style="
          position:absolute;
          left:50%;
          top:${tailTop}px;
          width:${tailSize}px;
          height:${tailSize}px;
          transform:translateX(-50%) rotate(45deg);
          background:${MARKER_BORDER_COLOR};
          box-sizing:border-box;
          z-index:0;
        "
      ></div>
      <div
        style="
          position:absolute;
          inset:0 0 auto 0;
          width:${size}px;
          height:${size}px;
          border-radius:50%;
          background:${color};
          border:3px solid ${MARKER_BORDER_COLOR};
          box-sizing:border-box;
          display:flex;
          align-items:center;
          justify-content:center;
          color:${MARKER_TEXT_COLOR};
          font-family:'Open Sans', sans-serif;
          font-size:${fontSize}px;
          font-weight:600;
          line-height:1;
          z-index:1;
        "
      >${count ?? ""}</div>
    </div>
  `
}

function createMarkerIcon(color?: string | null) {
  const size = 30
  const height = size + Math.round(size * 0.28)

  return L.divIcon({
    className: "",
    iconSize: [size, height],
    iconAnchor: [size / 2, height - 2],
    popupAnchor: [0, -height + 8],
    html: createPinHtml({ color: color ?? MARKER_COLOR, size })
  })
}

function getRiskColor(risk: PhysicalRisk | null | undefined): string {
  if (!risk) return MARKER_COLOR
  if (risk.cValueEffectiveColor) return risk.cValueEffectiveColor
  const scale = RISK_RESIDUAL_SCALES[risk.cValueEffectiveIndex]
  return scale ? getColor(scale.color) || MARKER_COLOR : MARKER_COLOR
}

function getPhysicalRiskScore(risk: PhysicalRisk | null | undefined): number {
  if (!risk) return -1
  if (typeof risk.cValueEffectiveIndex === "number") return risk.cValueEffectiveIndex
  if (typeof risk.cValueEffective === "number") return risk.cValueEffective
  return (risk.severity * risk.frequency) / Math.max(1, risk.prevention)
}

function getClusterRiskColor(cluster: { getAllChildMarkers: () => any[] }) {
  return cluster.getAllChildMarkers().reduce(
    (maxRisk, marker) => {
      let data: { color?: string; riskScore?: number } = {}
      try {
        data = JSON.parse(marker.options?.title ?? "{}")
      } catch {
        /* ignore */
      }
      const riskScore = data.riskScore ?? -1
      return riskScore > maxRisk.score ? { color: data.color ?? MARKER_COLOR, score: riskScore } : maxRisk
    },
    { color: MARKER_COLOR, score: -1 }
  ).color
}

function createClusterIcon(cluster: { getAllChildMarkers: () => any[]; getChildCount: () => number }) {
  const count = cluster.getChildCount()
  const size = Math.max(40, Math.min(58, 36 + String(count).length * 6))
  const height = size + Math.round(size * 0.28)
  const color = getClusterRiskColor(cluster)

  return L.divIcon({
    className: "",
    iconSize: [size, height],
    iconAnchor: [size / 2, height - 2],
    popupAnchor: [0, -height + 8],
    html: createPinHtml({ color, count, size })
  })
}

function getBounds(positions: [number, number][]) {
  if (!positions.length) return null
  return L.latLngBounds(positions.map(([lat, lng]) => [lat, lng]))
}

function toRadians(value: number) {
  return (value * Math.PI) / 180
}

function distanceKm([latA, lngA]: [number, number], [latB, lngB]: [number, number]) {
  const earthRadiusKm = 6371
  const dLat = toRadians(latB - latA)
  const dLng = toRadians(lngB - lngA)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(latA)) * Math.cos(toRadians(latB)) * Math.sin(dLng / 2) * Math.sin(dLng / 2)

  return 2 * earthRadiusKm * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function getCentroid(items: EstablishmentMapItem[]): [number, number] {
  const total = items.reduce(
    (acc, item) => ({
      lat: acc.lat + item.position[0],
      lng: acc.lng + item.position[1]
    }),
    { lat: 0, lng: 0 }
  )

  return [total.lat / items.length, total.lng / items.length]
}

function getBoundsDiagonalKm(items: EstablishmentMapItem[]) {
  const bounds = getBounds(items.map((item) => item.position))
  if (!bounds) return 0

  const southWest = bounds.getSouthWest()
  const northEast = bounds.getNorthEast()

  return distanceKm([southWest.lat, southWest.lng], [northEast.lat, northEast.lng])
}

function getClusterComponents(items: EstablishmentMapItem[]) {
  const visited = new Set<string>()
  const byId = new Map(items.map((item) => [item.id, item]))
  const neighbours = new Map<string, string[]>()

  items.forEach((item) => neighbours.set(item.id, []))

  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      if (distanceKm(items[i].position, items[j].position) <= DETACHED_CLUSTER_DISTANCE_KM) {
        neighbours.get(items[i].id)?.push(items[j].id)
        neighbours.get(items[j].id)?.push(items[i].id)
      }
    }
  }

  return items.reduce<EstablishmentMapItem[][]>((components, item) => {
    if (visited.has(item.id)) return components

    const stack = [item.id]
    const component: EstablishmentMapItem[] = []
    visited.add(item.id)

    while (stack.length) {
      const id = stack.pop()
      if (!id) continue

      const current = byId.get(id)
      if (current) component.push(current)

      neighbours.get(id)?.forEach((neighbourId) => {
        if (visited.has(neighbourId)) return
        visited.add(neighbourId)
        stack.push(neighbourId)
      })
    }

    return [...components, component]
  }, [])
}

function getDetachedMaps(items: EstablishmentMapItem[]): DetachedMap[] {
  if (items.length < 3) return []

  const components = getClusterComponents(items).sort((a, b) => b.length - a.length)
  if (components.length < 2) return []

  const [mainComponent, ...otherComponents] = components
  const mainCentroid = getCentroid(mainComponent)
  const mainDiagonal = getBoundsDiagonalKm(mainComponent)
  const globalDiagonal = getBoundsDiagonalKm(items)
  const mainComponentRatio = mainComponent.length / items.length

  //Une mini-map n'aide que face a un vrai groupe principal et quelques points excentres. Pour une
  //repartition plus equilibree, la carte principale doit simplement cadrer tous les etablissements.
  if (mainComponent.length < MIN_MAIN_CLUSTER_SIZE || mainComponentRatio < MIN_MAIN_CLUSTER_RATIO) return []

  const detachedComponents = otherComponents
    .filter((component) => {
      const centroidDistance = distanceKm(mainCentroid, getCentroid(component))
      const componentRatio = component.length / items.length

      return (
        component.length < mainComponent.length &&
        componentRatio <= MAX_DETACHED_COMPONENT_RATIO &&
        centroidDistance >= Math.max(MIN_DETACHED_DISTANCE_KM, mainDiagonal * 0.65) &&
        globalDiagonal >= Math.max(MIN_DETACHED_DISTANCE_KM * 1.4, mainDiagonal * 1.45)
      )
    })
    .sort((a, b) => a.length - b.length)
    .slice(0, MAX_DETACHED_MAPS)

  const usedCorners = new Set<DetachedMapCorner>()

  return detachedComponents.flatMap((component) => {
    const bounds = getBounds(component.map((item) => item.position))
    if (!bounds) return []

    //La mini-map reprend le cote geographique du groupe : ouest a gauche, est a droite.
    //En cas de conflit, on utilise le coin libre pour ne pas superposer deux mini-maps.
    const isEastOfMainCluster = getCentroid(component)[1] >= mainCentroid[1]
    const preferredCorner: DetachedMapCorner = isEastOfMainCluster ? "right" : "left"
    const fallbackCorner: DetachedMapCorner = preferredCorner === "left" ? "right" : "left"
    const corner = usedCorners.has(preferredCorner) ? fallbackCorner : preferredCorner
    usedCorners.add(corner)

    return [
      {
        id: component
          .map((item) => item.id)
          .sort()
          .join("|"),
        items: component,
        bounds,
        corner
      }
    ]
  })
}

function parseCoordinate(value: unknown) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (typeof value !== "string") return null

  const parsed = Number(value.replace(",", "."))
  return Number.isFinite(parsed) ? parsed : null
}

interface FitBoundsPadding {
  padding?: [number, number]
  paddingBottomRight?: [number, number]
  paddingTopLeft?: [number, number]
}

function FitBounds({
  enabled = true,
  padding,
  paddingBottomRight,
  paddingTopLeft,
  positions
}: FitBoundsPadding & { enabled?: boolean; positions: [number, number][] }) {
  const map = useMap()

  useEffect(() => {
    if (!enabled) return

    const bounds = getBounds(positions)
    if (!bounds) return

    map.fitBounds(bounds, {
      padding: padding ?? [40, 40],
      paddingBottomRight,
      paddingTopLeft,
      maxZoom: 13
    })
  }, [enabled, map, padding, paddingBottomRight, paddingTopLeft, positions])

  return null
}

function MapHomeButton({
  bounds,
  onClick,
  padding,
  paddingBottomRight,
  paddingTopLeft
}: FitBoundsPadding & { bounds: L.LatLngBounds; onClick?: () => void }) {
  const map = useMap()

  return (
    <button
      onClick={() => {
        onClick?.()
        map.fitBounds(bounds, {
          padding: padding ?? [40, 40],
          paddingBottomRight,
          paddingTopLeft,
          maxZoom: 13
        })
      }}
      className="absolute right-[12px] top-[12px] z-[1000] flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-[12px] bg-white shadow hover:bg-gray-50"
      title="Recentrer la carte">
      <FontAwesomeIcon icon={faHouse} className="text-sm text-gray-700" />
    </button>
  )
}

function EstablishmentMarkers({
  items,
  getMarkerIcon,
  onClusterClick,
  onMarkerClick,
  onConnect,
  zoomToBoundsOnClick = false
}: {
  items: EstablishmentMapItem[]
  getMarkerIcon: (color: string) => L.DivIcon
  onClusterClick?: (event: L.LeafletMouseEvent) => void
  onMarkerClick?: (item: EstablishmentMapItem) => void
  onConnect?: (id: string) => void
  zoomToBoundsOnClick?: boolean
}) {
  return (
    <MarkerClusterGroup
      key={items.map((i) => `${i.id}:${i.color}`).join("|")}
      chunkedLoading
      maxClusterRadius={10}
      zoomToBoundsOnClick={zoomToBoundsOnClick}
      onClick={onClusterClick}
      iconCreateFunction={(cluster) => createClusterIcon(cluster)}>
      {items.map((e) => (
        <Marker
          key={e.id}
          title={JSON.stringify({ color: e.color, riskScore: e.riskScore })}
          position={e.position}
          eventHandlers={{
            click: () => onMarkerClick?.(e)
          }}
          icon={getMarkerIcon(e.color)}>
          <Popup offset={[0, -10]}>
            <div className="text-sm leading-tight">
              <div className="flex items-center gap-1 font-semibold">
                <FontAwesomeIcon icon={faBuilding} className="grey-t" />
                {e.name}
              </div>

              {e.siret && <div className="grey-t mt-1 text-sm">SIRET : {e.siret}</div>}

              {e.naf && (
                <div className="mt-1 flex items-center gap-1">
                  <FontAwesomeIcon icon={faTag} className="grey-t" />
                  NAF : {e.naf}
                </div>
              )}

              {e.address && (
                <div className="mt-1 flex items-center gap-1">
                  <FontAwesomeIcon icon={faLocationDot} className="grey-t" />
                  {e.address}
                </div>
              )}

              {onConnect && (
                <Button className="primary" classNameCustom="mt-2" isFullWidth onClick={() => onConnect(e.id)}>
                  Se connecter
                </Button>
              )}
            </div>
          </Popup>
        </Marker>
      ))}
    </MarkerClusterGroup>
  )
}

function DetachedEstablishmentsMap({
  detachedMap,
  getMarkerIcon,
  onClose,
  onMarkerClick,
  onConnect
}: {
  detachedMap: DetachedMap
  getMarkerIcon: (color: string) => L.DivIcon
  onClose: () => void
  onMarkerClick: (detachedMap: DetachedMap, item: EstablishmentMapItem) => void
  onConnect?: (id: string) => void
}) {
  const cornerClass = detachedMap.corner === "left" ? "left-[-1px]" : "right-[-1px]"

  return (
    <div
      style={{ border: "1px solid grey", borderRadius: "12px 0px 0px 0px" }}
      className={`absolute bottom-[-1px] ${cornerClass} z-[1000] h-[190px] w-[280px] overflow-hidden bg-white shadow-lg`}>
      <button
        onClick={onClose}
        className="absolute right-[8px] top-[8px] z-[1001] flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-[12px] bg-white shadow hover:bg-gray-50"
        title="Fermer la carte annexe">
        <FontAwesomeIcon icon={faTimes} className="text-xs text-gray-700" />
      </button>

      <MapContainer
        center={detachedMap.bounds.getCenter()}
        zoom={8}
        scrollWheelZoom={false}
        attributionControl={false}
        zoomControl={false}
        className="h-full w-full">
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          className="grayscale contrast-[1.08]"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds positions={detachedMap.items.map((item) => item.position)} />
        <EstablishmentMarkers
          items={detachedMap.items}
          getMarkerIcon={getMarkerIcon}
          onClusterClick={() => onMarkerClick(detachedMap, detachedMap.items[0])}
          onMarkerClick={(item) => onMarkerClick(detachedMap, item)}
          onConnect={onConnect}
        />
      </MapContainer>
    </div>
  )
}

function toMapItem(establishment: Establishment): EstablishmentMapItem | null {
  const x = parseCoordinate(establishment.x)
  const y = parseCoordinate(establishment.y)

  if (x === null || y === null) return null

  const bestRisk = establishment.riskMax

  return {
    id: establishment.id,
    siret: establishment.codeEstablishment,
    name: establishment.name,
    address: establishment.address ?? "",
    color: getRiskColor(bestRisk),
    riskScore: getPhysicalRiskScore(bestRisk),
    position: [y, x],
    naf: establishment.industryCode ?? ""
  }
}

export default function EstablishmentsMap({ establishments, onConnect }: Props) {
  const [closedDetachedMapIds, setClosedDetachedMapIds] = useState<Set<string>>(() => new Set())
  const [focusedDetachedMapId, setFocusedDetachedMapId] = useState<string | null>(null)
  const [areDetachedMapsHidden, setAreDetachedMapsHidden] = useState(false)
  const mapItems = useMemo(
    () =>
      establishments
        .filter((establishment) => !establishment.isDisabled)
        .map(toMapItem)
        .filter((item): item is EstablishmentMapItem => item !== null),
    [establishments]
  )

  const detachedMaps = useMemo(() => getDetachedMaps(mapItems), [mapItems])
  const visibleDetachedMaps = useMemo(
    () =>
      focusedDetachedMapId || areDetachedMapsHidden
        ? []
        : detachedMaps.filter((detachedMap) => !closedDetachedMapIds.has(detachedMap.id)),
    [areDetachedMapsHidden, closedDetachedMapIds, detachedMaps, focusedDetachedMapId]
  )
  const focusedDetachedMap = useMemo(
    () => detachedMaps.find((detachedMap) => detachedMap.id === focusedDetachedMapId) ?? null,
    [detachedMaps, focusedDetachedMapId]
  )
  const visibleDetachedIds = useMemo(
    () => new Set(visibleDetachedMaps.flatMap((detachedMap) => detachedMap.items.map((item) => item.id))),
    [visibleDetachedMaps]
  )
  const mainMapPadding = useMemo(() => {
    const hasLeftDetachedMap = visibleDetachedMaps.some((detachedMap) => detachedMap.corner === "left")
    const hasRightDetachedMap = visibleDetachedMaps.some((detachedMap) => detachedMap.corner === "right")

    return {
      paddingTopLeft: [hasLeftDetachedMap ? 260 : 40, 40] as [number, number],
      paddingBottomRight: [hasRightDetachedMap ? 260 : 40, 40] as [number, number]
    }
  }, [visibleDetachedMaps])
  const mainMapItems = useMemo(() => {
    if (focusedDetachedMap) return focusedDetachedMap.items
    if (areDetachedMapsHidden) {
      const detachedIds = new Set(detachedMaps.flatMap((detachedMap) => detachedMap.items.map((item) => item.id)))
      return mapItems.filter((item) => !detachedIds.has(item.id))
    }
    return mapItems.filter((item) => !visibleDetachedIds.has(item.id))
  }, [areDetachedMapsHidden, detachedMaps, focusedDetachedMap, mapItems, visibleDetachedIds])
  const positions = useMemo(() => mainMapItems.map((e) => e.position), [mainMapItems])
  const initialBounds = useMemo(() => getBounds(positions), [positions])
  const markerIcons = useMemo(() => new Map<string, L.DivIcon>(), [])

  useEffect(() => {
    setClosedDetachedMapIds((current) => {
      const detachedIds = new Set(detachedMaps.map((detachedMap) => detachedMap.id))
      const next = new Set([...current].filter((id) => detachedIds.has(id)))

      return next.size === current.size ? current : next
    })
    setFocusedDetachedMapId((current) =>
      current && detachedMaps.some((detachedMap) => detachedMap.id === current) ? current : null
    )
  }, [detachedMaps])

  function getMarkerIcon(color: string) {
    const cachedIcon = markerIcons.get(color)
    if (cachedIcon) return cachedIcon

    const icon = createMarkerIcon(color)
    markerIcons.set(color, icon)
    return icon
  }

  function handleMainClusterClick() {
    handleMainMarkerClick()
  }

  function handleMainMarkerClick() {
    if (focusedDetachedMapId) return

    setAreDetachedMapsHidden(true)
  }

  function handleDetachedMarkerClick(detachedMap: DetachedMap) {
    setFocusedDetachedMapId(detachedMap.id)
    setAreDetachedMapsHidden(false)
  }

  function handleHomeClick() {
    setFocusedDetachedMapId(null)
    setAreDetachedMapsHidden(false)
    setClosedDetachedMapIds(new Set())
  }

  return (
    <div
      style={{ border: "1px solid grey" }}
      className="relative mb-20 h-[560px] w-full overflow-hidden rounded-[12px] shadow-md">
      <MapContainer center={FRANCE_CENTER} zoom={5} scrollWheelZoom={false} className="h-full w-full">
        <TileLayer
          attribution="&copy; OpenStreetMap contributors"
          className="grayscale contrast-[1.08]"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {initialBounds && <MapHomeButton bounds={initialBounds} onClick={handleHomeClick} {...mainMapPadding} />}
        {positions.length > 0 && (
          <FitBounds enabled={!areDetachedMapsHidden} positions={positions} {...mainMapPadding} />
        )}

        <EstablishmentMarkers
          items={mainMapItems}
          getMarkerIcon={getMarkerIcon}
          onClusterClick={handleMainClusterClick}
          onMarkerClick={handleMainMarkerClick}
          onConnect={onConnect}
          zoomToBoundsOnClick
        />
      </MapContainer>

      {visibleDetachedMaps.map((detachedMap) => (
        <DetachedEstablishmentsMap
          key={detachedMap.id}
          detachedMap={detachedMap}
          getMarkerIcon={getMarkerIcon}
          onClose={() =>
            setClosedDetachedMapIds((current) => {
              const next = new Set(current)
              next.add(detachedMap.id)
              return next
            })
          }
          onMarkerClick={handleDetachedMarkerClick}
          onConnect={onConnect}
        />
      ))}
    </div>
  )
}
